import React, {useEffect, useState} from "react";
import {Box, Drawer, Toolbar, useMediaQuery} from "@mui/material";
import MuiAppBar from '@mui/material/AppBar';
import {green} from "@mui/material/colors";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './Layout.css';
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import {useToolbar} from "./ToolbarContext";
import { onAuthStateChanged } from "firebase/auth";
import { firebaseAuth } from "../shared/services/firebase.service";
import {usePermissions} from "../shared/permissions/PermissionContext";
import {PermissionCode} from "../shared/permissions/permissions";
import {
  CalendarIcon, ClientsIcon, EquipmentIcon, EstimatesIcon, HomeIcon, InvoicesIcon, JobsIcon, LibraryIcon,
  PricingIcon, SettingsIcon, TeamIcon,
} from "./menu/MenuIcons";

export interface LayoutProps {
  children?: React.ReactNode;
}

/**
 * The layer over the green bar that a record page draws its header into
 * (status and Edit/Save, over the record column; SQ-574 rule 4), with
 * `createPortal(header, document.getElementById(BAR_LAYER_ID))`.
 */
export const BAR_LAYER_ID = 'app-bar-layer';

/*
 * The app's frame (SQ-554): the green bar across the top with a Menu button
 * at its left, and the menu down the left side. From PINNED_MIN wide the menu
 * stays open beside the page (Menu and Close hide and show it, remembered per
 * browser); narrower, it slides out over the page and closes when an item is
 * picked. The items, their order, icons and active style are Android's
 * (estimatebuilder/menu/BottomMenuBar.kt, MENU_SECTIONS), Home first and
 * Settings last. There is no bottom bar: that is the phone's.
 */

interface Section {
  key: string;
  label: string;
  path: string;
  Icon: (props: React.SVGProps<SVGSVGElement>) => React.ReactElement;
  visible?: boolean;
}

const useSections = (): Section[] => {
  const {me, hasPermission, hasAnyPermission} = usePermissions();
  return [
    {key: 'home', label: 'Home', path: '/home', Icon: HomeIcon},
    {key: 'clients', label: 'Clients', path: '/clients', Icon: ClientsIcon,
      visible: hasPermission(PermissionCode.ClientsViewList)},
    {key: 'appointments', label: 'Appointments', path: '/appointments', Icon: CalendarIcon,
      visible: hasPermission(PermissionCode.AppointmentsViewList)},
    {key: 'estimates', label: 'Estimates', path: '/estimates', Icon: EstimatesIcon,
      visible: hasPermission(PermissionCode.EstimatesViewList)},
    {key: 'jobs', label: 'Jobs', path: '/jobs', Icon: JobsIcon,
      visible: hasAnyPermission(PermissionCode.JobsViewAll, PermissionCode.JobsViewAssigned)},
    {key: 'invoices', label: 'Invoices', path: '/invoices', Icon: InvoicesIcon,
      visible: hasPermission(PermissionCode.InvoicesViewList)},
    {key: 'equipment', label: 'Equipment', path: '/equipment', Icon: EquipmentIcon,
      visible: hasPermission(PermissionCode.EquipmentViewList)},
    {key: 'team', label: 'Team Members', path: '/settings/access', Icon: TeamIcon,
      visible: hasPermission(PermissionCode.RbacManage)},
    {key: 'pricing', label: 'Job Pricing', path: '/settings/pricing', Icon: PricingIcon,
      visible: hasPermission(PermissionCode.SettingsPricing)},
    // Web only: Android has no library.
    {key: 'library', label: me?.isAdmin ? 'Admin · Library' : 'Comparable library', path: '/admin/library', Icon: LibraryIcon,
      visible: !!(me?.isAdmin || me?.canCurateLibrary)},
    {key: 'settings', label: 'Settings', path: '/settings', Icon: SettingsIcon},
  ].filter(section => section.visible !== false);
};

/**
 * The item a path belongs to. A record page counts as its list's item; the
 * longest matching path wins, so /settings/access is Team Members, not Settings.
 */
const activeKey = (pathname: string, sections: Section[]) => {
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return 'library';
  if (pathname === ACCOUNT_PATH) return ACCOUNT_PATH;
  return sections
    .filter(s => pathname === s.path || pathname.startsWith(`${s.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]?.key;
};

/** The Account page, opened from who is signed in at the menu's foot (SQ-652). */
const ACCOUNT_PATH = '/account';

/** From this window width the menu stays open beside the page. */
const PINNED_MIN = 1440;
const PINNED_KEY = 'menuPinned';

const readPinned = () => {
  try { return window.localStorage.getItem(PINNED_KEY) !== 'false'; } catch { return true; }
};
const savePinned = (pinned: boolean) => {
  try { window.localStorage.setItem(PINNED_KEY, String(pinned)); } catch { /* a convenience only */ }
};

const initialsOf = (name?: string | null, email?: string | null) => {
  const source = name?.trim() || email?.trim();
  if (!source) return 'Me';
  // Words that start with a letter: a phone account with no name has none.
  const words = source.split(/[\s@.]+/).filter(w => /^\p{L}/u.test(w));
  if (words.length === 0) return 'Me';
  return words.slice(0, 2).map(w => w[0].toUpperCase()).join('');
};

interface MenuListProps {
  sections: Section[];
  active?: string;
  /** Who is signed in when the profile has no email (a phone account). */
  signedInAs?: string;
  /** Open beside the page (a wide window), not in the drawer. */
  pinned?: boolean;
  go: (path: string) => void;
  close: () => void;
}

const MenuList = ({sections, active, signedInAs, pinned, go, close}: MenuListProps) => {
  const {me} = usePermissions();
  const email = me?.email ?? signedInAs;
  return (
    <nav className={pinned ? 'app-menu app-menu--pinned' : 'app-menu'} aria-label="Menu">
      <div className="app-menu-head">
        <button className="app-menu-close" onClick={close} aria-label="Close the menu">
          <ArrowBackIcon/> Close
        </button>
      </div>
      <div className="app-menu-items">
        {sections.map(section => (
          <button key={section.key} className={section.key === active ? 'app-menu-item app-menu-item--active' : 'app-menu-item'}
                  onClick={() => go(section.path)} aria-current={section.key === active ? 'page' : undefined}>
            <span className="app-menu-icon-pill"><section.Icon/></span>
            <span>{section.label}</span>
          </button>
        ))}
      </div>
      <div className="app-menu-foot">
        {/* Who is signed in: the only way in to the Account page on the web (SQ-652). */}
        <button className={active === ACCOUNT_PATH ? 'app-menu-me app-menu-me--active' : 'app-menu-me'}
                onClick={() => go(ACCOUNT_PATH)} aria-current={active === ACCOUNT_PATH ? 'page' : undefined}
                aria-label={`Account: ${me?.name?.trim() || 'Your profile'}`}>
          <span className="app-menu-me-initials">{initialsOf(me?.name, email)}</span>
          <span className="app-menu-me-text">
            <span className="app-menu-me-name">{me?.name?.trim() || 'Your profile'}</span>
            {email && <span className="app-menu-me-email">{email}</span>}
          </span>
          <svg className="app-menu-me-arrow" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 5.5L15.5 12L9 18.5" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </nav>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const sections = useSections();

  const toolbarContext = useToolbar();

  // Layout renders outside of AuthGuard, so it can mount before Firebase has
  // finished restoring the persisted session (which happens asynchronously on a
  // page refresh). Reading currentUser synchronously here would momentarily look
  // unauthenticated and wrongly bounce the user to /login. Wait for the auth
  // state to resolve before deciding. `undefined` = still resolving.
  const [loggedInUser, setLoggedInUser] = useState<{ email: string } | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      setLoggedInUser(user ? { email: user.email || user.phoneNumber || user.uid } : null);
    });
    return unsubscribe;
  }, []);

  // The one breakpoint measured on the window: the menu is what decides the page area.
  const wide = useMediaQuery(`(min-width:${PINNED_MIN}px)`);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pinnedPref, setPinnedPref] = useState(readPinned);
  const pinned = wide && pinnedPref;
  const setPinned = (value: boolean) => { setPinnedPref(value); savePinned(value); };
  // A drawer left open does not come back when a widened window is narrowed again.
  useEffect(() => {
    if (wide) setDrawerOpen(false);
  }, [wide]);
  const toggleMenu = () => wide ? setPinned(!pinnedPref) : setDrawerOpen(open => !open);
  const go = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  // Still waiting for Firebase to restore the persisted session; render nothing
  // rather than redirecting, so a refresh doesn't kick the user to /login.
  if (loggedInUser === undefined) {
    return null;
  }

  // Resolved with no authenticated user — redirect to login rather than throwing,
  // which would crash the whole app.
  if (!loggedInUser) {
    return <Navigate to="/login" replace />;
  }

  const active = activeKey(pathname, sections);
  // The menu never slides over the page on a wide window.
  const menuShown = wide ? pinned : drawerOpen;

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      {/* The bar keeps the medium green it had before the theme's primary
          became GreenDeep for the buttons (SQ-538). */}
      <MuiAppBar position='relative' sx={{bgcolor: green[500], color: (t) => t.palette.getContrastText(green[500])}}>
        <Toolbar>
          {/* One name whatever the state; aria-expanded says whether the menu is showing. */}
          <button className="app-bar-menu-button" onClick={toggleMenu} aria-label="Menu" aria-expanded={menuShown}>
            <MenuIcon/>
            <span>Menu</span>
          </button>
          <Box sx={{flexGrow: 1}}/>
          {/* Pages that have not moved their header into the bar's layer yet (SQ-574) still put their buttons here. */}
          {toolbarContext.actions && <Box sx={{pl: 2, flexGrow: 0}}>
            {toolbarContext.actions.call(this, toolbarContext.toolbarData)}
          </Box>}
        </Toolbar>
        <div id={BAR_LAYER_ID} className="app-bar-layer"/>
      </MuiAppBar>
      <Drawer anchor="left" open={drawerOpen && !wide} onClose={() => setDrawerOpen(false)}>
        <MenuList sections={sections} active={active} signedInAs={loggedInUser.email} go={go} close={() => setDrawerOpen(false)}/>
      </Drawer>
      <Box sx={{display: 'flex', flexGrow: 1, minHeight: 0, width: '100%'}}>
        {pinned && <MenuList pinned sections={sections} active={active} signedInAs={loggedInUser.email} go={go} close={() => setPinned(false)}/>}
        <Box component="main" sx={{flexGrow: 1, minWidth: 0, overflow: 'auto', height: '100%'}}>
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default Layout;
